import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Images, ChevronRight, Sparkles } from "lucide-react";
import { sectionTheme } from "../theme/homeOsSectionThemes";
import { MOCK_PHOTOS } from "../data/memoriesMockData";

/* ════════════════════════════════════════════════════════════════════════
   MEMORY OF THE DAY

   A small, gentle widget for the Home OS Today/Mothership page that
   surfaces one family memory each day.

   Source of truth (in priority order):
     1. Local-only saved screensaver album preference at
        localStorage["homeos.memories.screensaver"] — if enabled and the
        album has photos, draw from that album.
     2. Otherwise, draw deterministically from MOCK_PHOTOS using the
        current date so the choice is stable for the day.

   Safety:
     • Reads ONLY localStorage and bundled mock data.
     • Does NOT modify Wall Display behaviour.
     • Does NOT touch the screensaver preference.
     • Does NOT upload or alter media.
   ════════════════════════════════════════════════════════════════════════ */

const SCREENSAVER_KEY = "homeos.memories.screensaver";

function readScreensaverPref() {
  try {
    const raw = localStorage.getItem(SCREENSAVER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Stable day index — same value across renders for the same calendar day. */
function dayIndex() {
  const d = new Date();
  // Days since 2026-01-01 (any fixed epoch works — we only need stability).
  return Math.floor(
    (Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) -
      Date.UTC(2026, 0, 1)) /
      86400000
  );
}

export function MemoryOfTheDayCard() {
  const navigate = useNavigate();
  const theme = sectionTheme("memories");

  const photo = useMemo(() => {
    const pool = MOCK_PHOTOS.filter((p) => p.type === "image");
    if (pool.length === 0) return null;

    const pref = readScreensaverPref();
    if (pref?.enabled && pref?.albumId) {
      const albumPool = pool.filter((p) => p.album === pref.albumId);
      if (albumPool.length > 0) {
        return albumPool[Math.abs(dayIndex()) % albumPool.length];
      }
    }
    return pool[Math.abs(dayIndex()) % pool.length];
  }, []);

  const goLibrary = () => navigate("/home-os/memories");

  return (
    <section data-testid="section-memory-of-the-day">
      <div
        className="relative rounded-[24px] overflow-hidden bg-white border border-[color:var(--ara-line-soft)] hover:shadow-none transition-shadow"
        style={{
          backgroundImage:
            "linear-gradient(white, white), " + theme.gradient,
          backgroundOrigin: "border-box",
          backgroundClip: "padding-box, border-box",
          border: "1px solid transparent",
        }}
        data-testid="memory-of-day-card"
      >
        <div className="grid grid-cols-[120px_1fr] sm:grid-cols-[180px_1fr] items-stretch">
          {/* Image / empty state */}
          <button
            onClick={goLibrary}
            className="relative aspect-square sm:aspect-auto sm:h-full     overflow-hidden focus:outline-none"
            aria-label={photo ? `Open ${photo.filename}` : "Open Memories library"}
            data-testid="memory-of-day-image"
          >
            {photo ? (
              <>
                <img
                  src={photo.thumb || photo.src}
                  alt={photo.filename}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 hover:scale-[1.04]"
                />
                <div className="absolute inset-0  from-black/15 via-transparent to-transparent pointer-events-none" />
              </>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center" data-testid="memory-of-day-empty-art">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm"
                  style={{ background: theme.gradient }}
                >
                  <Images className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
              </div>
            )}
          </button>

          {/* Copy + CTA */}
          <div className="p-4 sm:p-5 flex flex-col justify-between min-w-0">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.16em] text-[color:var(--ara-ink-muted)] bg-transparent border border-[color:var(--ara-line-soft)] px-2 py-0.5 rounded-full">
                <Sparkles className="w-3 h-3" strokeWidth={1.5} />
                Memory of the day
              </div>
              <h3 className="text-lg sm:text-xl font-extrabold text-[var(--ara-ink)] mt-2 tracking-tight leading-tight">
                {photo ? "A little whānau moment for today." : "Add a few memories to see one here."}
              </h3>
              {photo ? (
                <p
                  className="text-[12px] font-bold text-[var(--ara-ink-muted)] mt-1 truncate"
                  data-testid="memory-of-day-caption"
                >
                  {photo.filename}
                  {photo.date ? ` · ${photo.date}` : ""}
                </p>
              ) : (
                <p className="text-[12px] font-medium text-[var(--ara-ink-muted)] mt-1 leading-snug">
                  Your family library is empty — upload a few photos to bring this card to life.
                </p>
              )}
            </div>

            <div className="mt-3">
              <button
                onClick={goLibrary}
                className="inline-flex items-center gap-1.5 bg-[color:var(--ara-ink)] hover:opacity-80 text-white text-[12px] font-extrabold px-3.5 py-2 rounded-full shadow-sm active:scale-[0.98] transition-all"
                data-testid="memory-of-day-cta"
              >
                Open library
                <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default MemoryOfTheDayCard;
